
import { Investor } from "@/types/investor";
import { enrichmentDebugState } from "../debugUtils";
import { parseJsonResponse } from "../parsingUtils";

/**
 * Function to enrich data using Perplexity
 */
export async function enrichWithPerplexity(
  investor: Investor,
  relevanceContext: string,
  apiKey: string
): Promise<Investor> {
  try {
    // Create an improved prompt for enriching investor data with emphasis on accurate information
    const prompt = `
      Perform thorough research on ${investor.name || "Unknown"} from ${investor.firm} venture capital firm.
      
      IMPORTANT INSTRUCTIONS:
      1. First check the VC firm's official website (${investor.website || investor.firm.toLowerCase().replace(/\s+/g, "") + ".com"}) for accurate information
      2. ONLY list portfolio companies where there is CLEAR EVIDENCE the firm was an investor (at any stage)
      3. Each fact must be supported by a credible source (firm website, Crunchbase, PitchBook, etc.)
      4. Do NOT guess or infer information - only state what can be verified
      5. For portfolio companies, include direct investments ONLY with verifiable proof
      6. If insufficient information is found, clearly state this rather than guessing
      7. Return sources for EACH piece of information you provide
      8. RESPOND WITH VALID JSON. Make sure your response can be parsed by JSON.parse() without errors.
      
      RESEARCH FOCUS:
      - Investment Focus: What sectors, stages, check sizes, and geographies do they focus on?
      - Portfolio Companies: List 5-10 notable companies with VERIFIED investments (with evidence)
      - Keywords/Interests: Extract from verified focus areas
      - Website: The official website URL of the firm
      - Relevance to: ${relevanceContext || "Technology startup seeking investment"}
      
      For each portfolio company, include the company name AND website in format: "Company Name | https://company-website.com"
      
      Return the following JSON object:
      {
        "investmentFocus": "Detailed description of their investment focus",
        "portfolioCompanies": ["Company 1 | https://company1.com", "Company 2 | https://company2.com"],
        "keywords": ["keyword1", "keyword2", "keyword3"],
        "relevanceScore": 7.5,
        "website": "https://example.com",
        "confidence": "high/medium/low",
        "notes": "Brief summary of data quality and sources",
        "sources": ["https://source1.com", "https://source2.com"]
      }
      
      EXTREMELY IMPORTANT: Be rigorous and accurate in your relevance scoring. The relevance score must reflect how aligned the investor is with: ${relevanceContext || "Technology startup seeking investment"}
      
      Ensure your response is valid, well-formed JSON with no trailing commas, properly escaped quotes, and all arrays properly terminated.
      
      Use the full range from 1-10, where:
      1-3 = Poor fit (minimal overlap with focus areas)
      4-6 = Moderate fit (some overlap but not ideal)
      7-8 = Good fit (significant overlap with focus areas)
      9-10 = Excellent fit (perfect alignment with focus areas)
    `;

    // Save the prompt for debugging
    enrichmentDebugState.lastPrompt = prompt;
    enrichmentDebugState.timestamp = new Date().toISOString();
    const startTime = Date.now();

    // Make request to Perplexity API - FIXING ALL PARAMETERS TO MATCH PERPLEXITY'S REQUIREMENTS
    const response = await fetch("https://api.perplexity.ai/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: "sonar", // Updated to working Perplexity model
        messages: [
          {
            role: "system",
            content: "You are a venture capital research specialist. Your task is to find accurate information about investors and VC firms using online sources. Always verify information with multiple sources when possible, prioritizing official websites. Never invent or hallucinate information. Clearly indicate when information cannot be verified. ALWAYS PROVIDE A VALID JSON RESPONSE WITH PROPERLY FORMATTED ARRAYS AND NO TRAILING COMMAS."
          },
          {
            role: "user",
            content: prompt
          }
        ],
        temperature: 0.2, // Lower temperature for more factual responses
        max_tokens: 1500
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Perplexity API error:", errorData);
      throw new Error(errorData.error?.message || "Failed to enrich investor data with Perplexity");
    }

    const data = await response.json();
    const content = data.choices?.[0]?.message?.content;
    
    if (!content) {
      throw new Error("Empty or invalid response received from Perplexity API");
    }
    
    const citations = data.choices?.[0]?.message?.citations || data.citations || [];
    
    // Save sources to debug state
    enrichmentDebugState.sources = citations.map((citation: any) => ({
      url: citation.url,
      title: citation.title || citation.url,
      snippet: citation.snippet || ""
    }));
    
    // Save the response and timing for debugging
    enrichmentDebugState.lastResponse = content;
    enrichmentDebugState.responseTime = Date.now() - startTime;
    enrichmentDebugState.model = data.model || "sonar-deep-research";
    
    try {
      // Parse the JSON response with our enhanced parser that can handle malformed JSON
      const enrichedData = parseJsonResponse(content);

      // Extract sources from Perplexity response
      const sources = enrichedData.sources || citations.map((citation: any) => citation.url);

      // Add confidence information to the investor record
      const confidence = enrichedData.confidence || "medium";
      const notes = enrichedData.notes || "";
      
      // Return investor with enriched data
      return {
        ...investor,
        investmentFocus: enrichedData.investmentFocus || investor.investmentFocus,
        portfolioCompanies: enrichedData.portfolioCompanies || investor.portfolioCompanies,
        keywords: enrichedData.keywords || investor.keywords,
        relevanceScore: enrichedData.relevanceScore || investor.relevanceScore,
        website: enrichedData.website || investor.website,
        lastRelevanceUpdate: new Date().toISOString(),
        confidence: confidence,
        notes: notes,
        sources: sources,
        enrichedWith: "perplexity" // Make sure this is one of the allowed values in the updated type
      };
    } catch (parseError) {
      console.error("Error parsing Perplexity response:", parseError);
      throw parseError;
    }
  } catch (error) {
    console.error("Error enriching with Perplexity:", error);
    throw error; // Re-throw to allow fallback to OpenAI
  }
}
